import { Injectable, signal, computed, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PermissionsService } from '@auth/services/permissions.service';
import { PERMISSION_MODULES } from '@auth/constants';

export type ModuleType =
  | 'dashboard'
  | 'sales'
  | 'purchases'
  | 'inventory'
  | 'crm'
  | 'finance'
  | 'accounting'
  | 'manufacturing'
  | 'rrhh'
  | 'payroll'
  | 'ecommerce'
  | 'settings'
  | 'tributario'
  | 'admin'
  | 'pos'
  | 'soporte'
  | 'proyectos';

export interface AppModule {
  id: ModuleType;
  label: string;
  icon: string;
  visible?: boolean;
  routePatterns: string[]; // Patrones de rutas que pertenecen a este módulo
  defaultRoute?: string; // Ruta por defecto al seleccionar el módulo
  permissionModuleId?: string | string[]; // ID del módulo de permisos del backend (ej: 'erp-ventas')
}

@Injectable({
  providedIn: 'root',
})
export class ModuleService {
  private router = inject(Router);
  private permissionsService = inject(PermissionsService);

  // Estado del módulo seleccionado actualmente
  private selectedModuleSignal = signal<ModuleType>('dashboard');

  // Signal de solo lectura para acceso externo
  selectedModule = computed(() => this.selectedModuleSignal());

  // Computed que retorna solo los módulos visibles según permisos del usuario
  visibleModules = computed(() => {
    return this.modules.filter((module) => {
      // Dashboard siempre visible
      if (module.id === 'dashboard') return true;

      // Admin solo para administradores
      if (module.id === 'admin') {
        // Aquí podrías usar authService.isAdmin() si lo necesitas
        return true; // Por ahora dejamos que se maneje con visible en el módulo
      }

      // Si tiene permissionModuleId, verificar acceso
      if (module.permissionModuleId) {
        if (Array.isArray(module.permissionModuleId)) {
          return this.permissionsService.hasAnyModuleAccess(module.permissionModuleId);
        }
        return this.permissionsService.hasModuleAccess(module.permissionModuleId as string);
      }

      // Si no tiene restricciones de permisos, mostrar
      return true;
    });
  });

  // Definición de módulos disponibles con sus patrones de ruta
  modules: AppModule[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'pi pi-home',
      routePatterns: ['/dashboard', '/'],
      defaultRoute: '/dashboard',
      // Dashboard no requiere permisos específicos
    },
    {
      id: 'sales',
      label: 'Ventas',
      icon: 'pi pi-shopping-bag',
      routePatterns: ['/sales'],
      defaultRoute: '/sales',
      permissionModuleId: [PERMISSION_MODULES.VENTAS],
    },
    {
      id: 'purchases',
      label: 'Compras',
      icon: 'pi pi-cart-arrow-down',
      routePatterns: ['/purchases'],
      defaultRoute: '/purchases',
      permissionModuleId: PERMISSION_MODULES.COMPRAS,
    },
    {
      id: 'inventory',
      label: 'Inventario',
      icon: 'pi pi-box',
      routePatterns: ['/inventory'],
      defaultRoute: '/inventory',
      permissionModuleId: PERMISSION_MODULES.INVENTARIO,
    },
    {
      id: 'crm',
      label: 'CRM',
      icon: 'pi pi-users',
      routePatterns: ['/crm'],
      defaultRoute: '/crm',
      permissionModuleId: 'erp-crm',
    },
    {
      id: 'finance',
      label: 'Finanzas',
      icon: 'pi pi-wallet',
      routePatterns: ['/finance'],
      defaultRoute: '/finance',
      permissionModuleId: PERMISSION_MODULES.FINANZAS,
    },
    {
      id: 'accounting',
      label: 'Contabilidad',
      icon: 'pi pi-book',
      routePatterns: ['/accounting'],
      defaultRoute: '/accounting/dashboard',
      permissionModuleId: PERMISSION_MODULES.CONTABILIDAD,
    },
    {
      id: 'manufacturing',
      label: 'Fabricación',
      icon: 'pi pi-cog',
      routePatterns: ['/manufacturing'],
      defaultRoute: '/manufacturing/boms',
      permissionModuleId: PERMISSION_MODULES.FABRICACION,
    },
    {
      id: 'rrhh',
      label: 'Recursos Humanos',
      icon: 'pi pi-id-card',
      routePatterns: ['/rrhh'],
      defaultRoute: '/rrhh/dashboard',
      permissionModuleId: PERMISSION_MODULES.RECURSOS_HUMANOS,
    },
    {
      id: 'payroll',
      label: 'Nómina',
      icon: 'pi pi-money-bill',
      routePatterns: ['/payroll'],
      defaultRoute: '/payroll/dashboard',
      permissionModuleId: PERMISSION_MODULES.NOMINA,
    },
    {
      id: 'ecommerce',
      label: 'E-Commerce',
      icon: 'pi pi-shopping-cart',
      routePatterns: ['/ecommerce'],
      defaultRoute: '/ecommerce',
      permissionModuleId: PERMISSION_MODULES.E_COMMERCE,
    },
    {
      id: 'tributario',
      label: 'Tributario',
      icon: 'pi pi-receipt',
      routePatterns: ['/tributario'],
      defaultRoute: '/tributario',
      permissionModuleId: PERMISSION_MODULES.TRIBUTARIO,
    },
    {
      id: 'pos',
      label: 'Punto de Venta',
      icon: 'pi pi-desktop',
      routePatterns: ['/pos/settings', '/pos'],
      defaultRoute: '/pos/settings/profiles',
      permissionModuleId: PERMISSION_MODULES.PUNTO_DE_VENTA,
    },
    {
      id: 'soporte',
      label: 'Soporte',
      icon: 'pi pi-headphones',
      routePatterns: ['/soporte'],
      defaultRoute: '/soporte/tickets',
      permissionModuleId: PERMISSION_MODULES.SOPORTE,
    },
    {
      id: 'proyectos',
      label: 'Proyectos y Tareas',
      icon: 'pi pi-objects-column',
      routePatterns: ['/proyectos'],
      defaultRoute: '/proyectos',
      permissionModuleId: PERMISSION_MODULES.PROYECTOS_Y_TAREAS,
    },
    {
      id: 'settings',
      label: 'Configuración',
      icon: 'pi pi-cog',
      routePatterns: ['/general-settings'],
      defaultRoute: '/general-settings',
      permissionModuleId: PERMISSION_MODULES.CONFIGURACIONES,
    },
    {
      id: 'admin',
      label: 'Admin',
      icon: 'pi pi-shield',
      routePatterns: ['/admin', '/soporte-admin'],
      defaultRoute: '/admin',
      // Admin no usa permisos del backend, se controla por isAdmin()
    },
  ];

  constructor() {
    // Detectar el módulo inicial basado en la URL actual
    this.detectModuleFromUrl(this.router.url);

    // Escuchar cambios de navegación para actualizar el módulo automáticamente
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.detectModuleFromUrl(event.urlAfterRedirects);
    });
  }

  /**
   * Detecta el módulo activo basándose en la URL actual
   */
  private detectModuleFromUrl(url: string): void {
    // Remover query params y fragments
    const cleanUrl = url.split('?')[0].split('#')[0];

    // Buscar el módulo que coincida con la URL
    const matchedModule = this.modules.find((module) =>
      module.routePatterns.some((pattern) => {
        // Si el patrón es exacto
        if (pattern === cleanUrl) return true;
        // Si la URL comienza con el patrón (para rutas hijas)
        if (cleanUrl.startsWith(pattern + '/')) return true;
        // Caso especial para dashboard en ruta raíz
        if (pattern === '/' && cleanUrl === '/') return true;
        return false;
      })
    );

    if (matchedModule) {
      this.selectedModuleSignal.set(matchedModule.id);
    } else {
      // Si no coincide ningún módulo, usar dashboard por defecto
      this.selectedModuleSignal.set('dashboard');
    }
  }

  /**
   * Cambia el módulo seleccionado manualmente y navega a la ruta por defecto
   */
  selectModule(moduleId: ModuleType): void {
    const module = this.modules.find((m) => m.id === moduleId);

    if (!module) {
      console.warn(`Módulo ${moduleId} no encontrado`);
      return;
    }

    // Actualizar el módulo seleccionado
    this.selectedModuleSignal.set(moduleId);

    // Navegar a la ruta por defecto si está definida
    if (module.defaultRoute) {
      this.router.navigate([module.defaultRoute]);
    }
  }

  /**
   * Verifica si un módulo está seleccionado
   */
  isModuleSelected(moduleId: ModuleType): boolean {
    return this.selectedModule() === moduleId;
  }
}
